import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthService } from '../services/auth.service';
import { DeepLinkHandlerService } from '../services/deep-link-handler.service';
import { MobilePushService } from '../native/mobile-push.service';
import { SdkHostBridgeService } from './sdk-host-bridge.service';
import { SdkRuntimeConfigService } from './sdk-runtime-config.service';

/**
 * F-SDK — the embedded entry point.
 *
 * In the standalone app the attendee arrives through a magic link, and
 * `DeepLinkHandlerService` drives everything from the URL. Inside a partner's
 * application there is no URL: the host calls `present(ssoToken:)`, the native
 * wrapper stashes the credential, and this service is what turns it into a
 * signed-in session.
 *
 * Also owns the two inverted-ownership handoffs. The host — not us — registers
 * with APNs/FCM and receives notification taps (REQ-F-SDK-040 / 041), because
 * an embedded SDK cannot own the application delegate on iOS and has no
 * Firebase project of its own on Android.
 */
@Injectable({ providedIn: 'root' })
export class SdkHostBootstrapService {
  private readonly runtimeConfig = inject(SdkRuntimeConfigService);
  private readonly bridge = inject(SdkHostBridgeService);
  private readonly auth = inject(AuthService);
  private readonly push = inject(MobilePushService);
  private readonly deepLink = inject(DeepLinkHandlerService);

  private installed = false;

  /**
   * Bootstrap hook. Runs after {@link SdkRuntimeConfigService.load} and after
   * `AuthService.initialize()`, so a still-valid session from a previous
   * presentation is reused rather than re-exchanged.
   */
  async install(): Promise<void> {
    if (this.installed || !this.runtimeConfig.isEmbedded()) return;
    this.installed = true;

    await this.installHostListeners();

    const credential = await this.bridge.consumeCredential();
    if (credential) {
      await this.exchangeCredential(credential);
      return;
    }

    // No credential and no session: the host presented the experience without
    // one. Tell it so, rather than silently showing our sign-in landing page,
    // which is a dead end inside a host app — there is no magic link to tap.
    if (!this.auth.isAuthenticated()) {
      await this.bridge.emit({ name: 'authenticationFailed', errorCode: 'credential-missing' });
    }
  }

  private async installHostListeners(): Promise<void> {
    await this.bridge.onPushToken(({ token, platform }) => {
      void this.push.registerHostPushToken(token, platform);
    });

    await this.bridge.onNotificationTap((payload) => {
      this.push.handleTap({ notificationId: payload.notificationId, data: payload.data ?? {} });
    });

    // The host may re-present the experience for a different user without the
    // process restarting — a plausible flow in a multi-account host app.
    await this.bridge.onCredentialUpdated(({ token }) => {
      if (token) {
        void this.exchangeCredential(token);
      }
    });

    // The host's own user signed out. Revoking here rather than only clearing
    // locally matters on a shared device: a persisted refresh token would let
    // the next presentation resume the previous attendee's session.
    await this.bridge.onSignOutRequested(() => {
      void this.auth.logout({ silent: true });
    });
  }

  private async exchangeCredential(credential: string): Promise<void> {
    try {
      const response = await this.auth.loginWithPartnerCredential(credential);

      this.deepLink.handleTarget(response.authContext.target);

      await this.bridge.emit({
        name: 'sessionEstablished',
        tenantSlug: response.authContext.tenant.tenantSlug,
        eventId: response.authContext.event?.eventId ?? null,
      });
    } catch (err) {
      const errorCode =
        err instanceof HttpErrorResponse
          ? readPartnerErrorCode(err) ?? `http-${err.status}`
          : 'unknown';

      // REQ-F-SDK-061 — the credential and any attendee identifiers must not
      // reach the host's console, so only the code is reported.
      console.warn('[SdkHostBootstrapService] Partner credential exchange failed:', errorCode);
      await this.bridge.emit({ name: 'authenticationFailed', errorCode });
    }
  }
}

function readPartnerErrorCode(err: HttpErrorResponse): string | null {
  const body = err.error as { errorCode?: unknown } | null | undefined;
  if (!body || typeof body !== 'object') return null;
  return typeof body.errorCode === 'string' ? body.errorCode : null;
}
